import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Save, FolderPlus, FileText, X, Loader } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import ImageSearch from '../../components/herramientas/ImageSearch';

interface Slot {
  url: string;
  label: string;
}

interface Config {
  rows: number;
  cols: number;
  orientation: 'portrait' | 'landscape';
}

type Screen = 'config' | 'builder' | 'library';

export default function HerramientasFichasPage() {
  const [screen, setScreen] = useState<Screen>('config');
  const [config, setConfig] = useState<Config>({ rows: 3, cols: 2, orientation: 'portrait' });
  const [slots, setSlots] = useState<(Slot | null)[]>([]);
  const [activeSlot, setActiveSlot] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Save modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveFilename, setSaveFilename] = useState('');
  const [saveFolder, setSaveFolder] = useState('');
  const [saving, setSaving] = useState(false);
  const [folders, setFolders] = useState<string[]>([]);

  // Library
  const [libFiles, setLibFiles] = useState<any[]>([]);
  const [libFolder, setLibFolder] = useState('');
  const [libLoading, setLibLoading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const total = slots.length;
  const filled = slots.filter(Boolean).length;

  function startBuilder() {
    setSlots(new Array(config.rows * config.cols).fill(null));
    setActiveSlot(0);
    setShowSearch(false);
    setScreen('builder');
  }

  function handleSelectImage(url: string, label: string) {
    const newSlots = [...slots];
    newSlots[activeSlot] = { url, label };
    setSlots(newSlots);
    const next = newSlots.findIndex((s, i) => s === null && i > activeSlot);
    if (next !== -1) setActiveSlot(next);
    setShowSearch(false);
  }

  function clearSlot(idx: number) {
    const newSlots = [...slots];
    newSlots[idx] = null;
    setSlots(newSlots);
    setActiveSlot(idx);
    setShowSearch(true);
  }

  function updateLabel(idx: number, label: string) {
    const newSlots = [...slots];
    if (newSlots[idx]) newSlots[idx] = { ...newSlots[idx]!, label };
    setSlots(newSlots);
  }

  async function generatePdf() {
    setGenerating(true);
    try {
      const response = await api.post('/rehab/fichas/generate-pdf', {
        slots: slots.filter(Boolean),
        config,
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ficha-logopedia.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al generar el PDF');
    } finally {
      setGenerating(false);
    }
  }

  async function openSaveModal() {
    try {
      const res = await api.get('/rehab/fichas/folders');
      if (res.data.ok) setFolders(res.data.data);
    } catch {}
    setShowSaveModal(true);
  }

  async function saveFicha() {
    if (!saveFilename.trim()) { toast.error('Escribe un nombre para el archivo'); return; }
    setSaving(true);
    try {
      await api.post('/rehab/fichas/save', {
        slots: slots.filter(Boolean),
        config,
        meta: { filename: saveFilename.trim(), folder: saveFolder.trim() || undefined },
      });
      setShowSaveModal(false);
      setSaveFilename('');
      setSaveFolder('');
      toast.success('Ficha guardada');
    } catch {
      toast.error('Error al guardar la ficha');
    } finally {
      setSaving(false);
    }
  }

  async function loadLibrary() {
    setLibLoading(true);
    setScreen('library');
    try {
      const [filesRes, foldersRes] = await Promise.all([
        api.get('/rehab/fichas/files'),
        api.get('/rehab/fichas/folders'),
      ]);
      if (filesRes.data.ok) setLibFiles(filesRes.data.data);
      if (foldersRes.data.ok) setFolders(foldersRes.data.data);
    } catch {
      toast.error('Error al cargar fichas');
    } finally {
      setLibLoading(false);
    }
  }

  async function loadLibraryByFolder(folder: string) {
    setLibFolder(folder);
    setLibLoading(true);
    try {
      const res = await api.get('/rehab/fichas/files', {
        params: folder ? { folder } : {},
      });
      if (res.data.ok) setLibFiles(res.data.data);
    } catch {} finally {
      setLibLoading(false);
    }
  }

  async function createFolder() {
    if (!newFolderName.trim()) return;
    try {
      await api.post('/rehab/fichas/folders', { name: newFolderName.trim() });
      setNewFolderName('');
      const res = await api.get('/rehab/fichas/folders');
      if (res.data.ok) setFolders(res.data.data);
      toast.success('Carpeta creada');
    } catch {
      toast.error('Error al crear la carpeta');
    }
  }

  // =================== PANTALLA CONFIG ===================
  if (screen === 'config') return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-lg">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <Link to="/herramientas" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Herramientas
          </Link>
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-display font-bold">Crear ficha</h1>
            <button
              onClick={loadLibrary}
              className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            >
              <FileText className="w-4 h-4" />
              Mis fichas
            </button>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="glass-container space-y-8">
          <h2 className="text-lg font-semibold">Configurar cuadrícula</h2>

          {/* Filas y columnas */}
          <div className="grid grid-cols-2 gap-6">
            {[
              { label: 'Filas', key: 'rows' as const, min: 1, max: 6 },
              { label: 'Columnas', key: 'cols' as const, min: 1, max: 5 },
            ].map(({ label, key, min, max }) => (
              <div key={key}>
                <label className="block text-sm text-white/60 mb-3">{label}</label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setConfig(c => ({ ...c, [key]: Math.max(min, c[key] - 1) }))}
                    className="w-9 h-9 rounded-full border border-white/20 font-bold text-lg hover:bg-white/10 transition-colors flex items-center justify-center"
                  >−</button>
                  <span className="text-3xl font-bold w-8 text-center">{config[key]}</span>
                  <button
                    onClick={() => setConfig(c => ({ ...c, [key]: Math.min(max, c[key] + 1) }))}
                    className="w-9 h-9 rounded-full border border-white/20 font-bold text-lg hover:bg-white/10 transition-colors flex items-center justify-center"
                  >+</button>
                </div>
              </div>
            ))}
          </div>

          {/* Orientación */}
          <div>
            <label className="block text-sm text-white/60 mb-3">Orientación</label>
            <div className="grid grid-cols-2 gap-3">
              {(['portrait', 'landscape'] as const).map(o => (
                <button
                  key={o}
                  onClick={() => setConfig(c => ({ ...c, orientation: o }))}
                  className={`py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    config.orientation === o
                      ? 'border-neureka-yellow bg-neureka-yellow/10 text-neureka-yellow'
                      : 'border-white/20 text-white/60 hover:border-white/40'
                  }`}
                >
                  {o === 'portrait' ? '📄 Vertical' : '📃 Horizontal'}
                </button>
              ))}
            </div>
          </div>

          <p className="text-center text-white/50 text-sm">
            {config.rows} × {config.cols} = <strong className="text-white">{config.rows * config.cols} imágenes</strong>
          </p>

          <button
            onClick={startBuilder}
            className="w-full py-4 bg-neureka-yellow text-neureka-dark font-bold text-lg rounded-xl hover:bg-yellow-400 transition-colors"
          >
            Empezar →
          </button>
        </motion.div>
      </div>
    </div>
  );

  // =================== PANTALLA BIBLIOTECA ===================
  if (screen === 'library') return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => setScreen('config')} className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Crear ficha
            </button>
            <h1 className="text-3xl font-display font-bold">Mis fichas</h1>
          </div>
        </motion.div>

        {/* Filtros de carpeta */}
        <div className="flex flex-wrap gap-2 mb-6 items-center">
          <button
            onClick={() => loadLibraryByFolder('')}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              libFolder === ''
                ? 'border-neureka-yellow bg-neureka-yellow/20 text-neureka-yellow'
                : 'border-white/20 text-white/60 hover:border-white/40'
            }`}
          >Todas</button>
          {folders.map(f => (
            <button
              key={f}
              onClick={() => loadLibraryByFolder(f)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                libFolder === f
                  ? 'border-neureka-yellow bg-neureka-yellow/20 text-neureka-yellow'
                  : 'border-white/20 text-white/60 hover:border-white/40'
              }`}
            >
              📁 {f}
            </button>
          ))}

          {/* Nueva carpeta inline */}
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createFolder()}
              placeholder="Nueva carpeta..."
              className="w-36 px-2 py-1 text-sm bg-white/5 border border-dashed border-white/20 rounded-full text-white placeholder-white/30 focus:outline-none focus:border-white/40"
            />
            <button onClick={createFolder} className="p-1.5 text-white/50 hover:text-white transition-colors">
              <FolderPlus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {libLoading ? (
          <div className="text-center py-16">
            <Loader className="w-8 h-8 animate-spin mx-auto text-neureka-yellow" />
          </div>
        ) : libFiles.length === 0 ? (
          <div className="glass-container text-center py-16 text-white/50">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            No hay fichas guardadas aquí
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {libFiles.map((f, i) => (
              <motion.a
                key={i}
                href={f.path}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="glass-container flex flex-col gap-2 hover:bg-white/10 transition-colors group"
              >
                <FileText className="w-8 h-8 text-orange-400 group-hover:text-orange-300 transition-colors" />
                <span className="text-sm font-medium truncate">{f.name}</span>
                {f.folder !== 'Raíz' && (
                  <span className="text-xs text-white/40">📁 {f.folder}</span>
                )}
                <span className="text-xs text-white/40">
                  {new Date(f.created).toLocaleDateString('es-ES')}
                </span>
              </motion.a>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // =================== PANTALLA BUILDER ===================
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/20">
        <div className="flex items-center gap-3">
          <button onClick={() => setScreen('config')} className="text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-semibold">Ficha</span>
          <span className="text-sm text-white/50">{filled}/{total}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadLibrary}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-white/20 rounded-lg text-white/70 hover:text-white hover:border-white/40 transition-colors"
          >
            <FileText className="w-4 h-4" />
            Mis fichas
          </button>
          <button
            onClick={openSaveModal}
            disabled={filled === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-white/20 rounded-lg text-white/70 hover:text-white hover:border-white/40 disabled:opacity-40 transition-colors"
          >
            <Save className="w-4 h-4" />
            Guardar
          </button>
          <button
            onClick={generatePdf}
            disabled={filled === 0 || generating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-neureka-yellow text-neureka-dark font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-40 transition-colors"
          >
            {generating ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            PDF
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-3xl mx-auto space-y-4">
        {/* Grid */}
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: `repeat(${config.cols}, 1fr)` }}
        >
          {slots.map((slot, idx) => (
            <div
              key={idx}
              onClick={() => {
                setActiveSlot(idx);
                if (!slot) setShowSearch(true);
              }}
              className={`aspect-square border-2 rounded-xl flex flex-col items-center justify-center relative cursor-pointer transition-colors
                ${slot
                  ? 'border-white/10 bg-white/5 hover:border-white/20'
                  : idx === activeSlot
                    ? 'border-neureka-yellow bg-neureka-yellow/10'
                    : 'border-dashed border-white/20 bg-white/5 hover:border-white/40'
                }`}
            >
              {slot ? (
                <>
                  <button
                    onClick={e => { e.stopPropagation(); clearSlot(idx); }}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center justify-center"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <img src={slot.url} alt={slot.label} className="w-full h-3/4 object-contain p-2" />
                  <input
                    type="text"
                    value={slot.label}
                    onChange={e => updateLabel(idx, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    className="w-full text-center text-xs font-bold uppercase border-t border-white/10 px-1 py-1 bg-transparent focus:outline-none text-white"
                  />
                </>
              ) : (
                <span className="text-3xl text-white/20">+</span>
              )}
            </div>
          ))}
        </div>

        {/* Buscador */}
        {showSearch && (
          <div className="glass-container">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-white/70">
                Imagen para celda {activeSlot + 1}
              </p>
              <button onClick={() => setShowSearch(false)} className="text-white/40 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <ImageSearch onSelect={handleSelectImage} />
          </div>
        )}

        {!showSearch && filled < total && (
          <button
            onClick={() => setShowSearch(true)}
            className="w-full py-3 border-2 border-dashed border-neureka-yellow/40 text-neureka-yellow/70 hover:border-neureka-yellow hover:text-neureka-yellow rounded-xl text-sm font-medium transition-colors"
          >
            Buscar imagen para celda {activeSlot + 1}
          </button>
        )}
        </div>
      </div>

      {/* Modal guardar */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-container w-full max-w-sm space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">Guardar ficha</h3>
              <button onClick={() => setShowSaveModal(false)} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Nombre del archivo</label>
              <input
                type="text"
                value={saveFilename}
                onChange={e => setSaveFilename(e.target.value)}
                placeholder="Ej: verbos-acciones"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-neureka-yellow"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Carpeta (opcional)</label>
              <input
                type="text"
                list="folder-list-save"
                value={saveFolder}
                onChange={e => setSaveFolder(e.target.value)}
                placeholder="Escribe o elige carpeta"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-neureka-yellow"
              />
              <datalist id="folder-list-save">
                {folders.map(f => <option key={f} value={f} />)}
              </datalist>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSaveModal(false)}
                className="flex-1 py-2 border border-white/20 rounded-lg text-white/70 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={saveFicha}
                disabled={saving}
                className="flex-1 py-2 bg-neureka-yellow text-neureka-dark font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
